import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { FileText, Activity, Target, AlertTriangle, DollarSign, Zap, Trophy, RotateCcw } from 'lucide-react';
import pipelineService, { PipelineRun } from '../../services/pipelineService';
import benchmarkService, { BenchmarkSummary } from '../../services/benchmarkService';
import { StatCard, PageHeader, StatusPill, EmptyState, Spinner, InfoHint, Button } from '../../components/ui';
import { HELP, prettyStream } from '../../lib/metrics';
import '../../styles/theme.css';

const AXIS = { fontSize: 11, fill: 'var(--mx-text-subtle)' };
const tooltipStyle = {
  background: 'var(--mx-surface)', border: '1px solid var(--mx-border)',
  borderRadius: 8, fontSize: 12, boxShadow: 'var(--mx-shadow-md)',
};
// Accuracy metrics are 0–1; show them as friendly percentages.
const pct = (v: number) => `${Math.round((v || 0) * 100)}%`;
const pctOrDash = (v?: number) => (v == null ? '—' : pct(v));

const OverviewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<any>(null);
  const [trend, setTrend] = useState<any[]>([]);
  const [streams, setStreams] = useState<any[]>([]);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [bench, setBench] = useState<BenchmarkSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [s, at, sc, r, b] = await Promise.all([
        pipelineService.summary(),
        pipelineService.accuracyTrend(),
        pipelineService.streamComparison(),
        pipelineService.listRuns(1, 8),
        // Benchmark is a secondary KPI — never let it fail the whole Overview.
        benchmarkService.summary().catch(() => ({ latest: null, trend: [] } as BenchmarkSummary)),
      ]);
      setSummary(s);
      setTrend((at.points || []).map((p: any, i: number) => ({ i, ...p })));
      setStreams(sc.streams || []);
      setRuns(r.runs || []);
      setBench(b);
    } catch {
      setError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div><PageHeader title="Overview" /><div className="mx-card mx-card-pad"><Spinner label="Loading metrics…" /></div></div>;

  if (error || !summary) return (
    <div><PageHeader title="Overview" />
      <div className="mx-card mx-card-pad" style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Couldn’t load metrics</div>
        <div className="mx-muted" style={{ fontSize: 13, marginBottom: 16 }}>The API may be restarting — try again.</div>
        <Button icon={RotateCcw} variant="primary" size="sm" onClick={load}>Retry</Button>
      </div>
    </div>
  );

  const f1Spark = trend.map((p) => p.f1).filter((v) => v != null);
  const halSpark = trend.map((p) => p.hallucination_rate).filter((v) => v != null);
  const energy = summary?.energy_joules || {};
  const benchLatest = bench?.latest || null;
  const benchSpark = (bench?.trend || []).map((t) => t.f1);

  return (
    <div>
      <PageHeader title="Overview" subtitle="How accurate the pipeline is, and what it costs, across all documents." />

      <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 16 }}>
        <StatCard label="Documents" value={summary.documents} icon={<FileText size={16} />} hint="processed" />
        <StatCard label="Runs" value={summary.runs} icon={<Activity size={16} />}
          hint={`${summary.exported} exported`} />
        <StatCard label="Accuracy" value={pctOrDash(summary.avg_f1)} icon={<Target size={16} />}
          help={HELP.accuracy} spark={f1Spark} sparkColor="var(--mx-c1)" />
        <StatCard label="Best method"
          value={benchLatest ? pct(benchLatest.f1) : '—'} icon={<Trophy size={16} />}
          help={HELP.bestAccuracy}
          spark={benchSpark.length ? benchSpark : undefined} sparkColor="var(--mx-c5)"
          hint={benchLatest ? `${prettyStream(benchLatest.winner || '')} · ${benchLatest.models} method${benchLatest.models === 1 ? '' : 's'}` : 'run a benchmark'}
          onClick={() => navigate('/admin/benchmark')} />
        <StatCard label="Made-up values" value={pctOrDash(summary.avg_hallucination_rate)}
          icon={<AlertTriangle size={16} />} help={HELP.madeUp}
          spark={halSpark} sparkColor="var(--mx-c4)" deltaGoodWhenUp={false} />
        <StatCard label="Total cost" value={`$${(summary.total_cost_usd || 0).toFixed(2)}`}
          icon={<DollarSign size={16} />} help={HELP.cost} />
        <StatCard label="Energy (est.)" value={`${Math.round(energy.estimated || 0)} J`} icon={<Zap size={16} />}
          help={HELP.energyEstimated} hint={`${Math.round(energy.measured || 0)} J measured`} />
      </div>

      <div className="mx-grid" style={{ gridTemplateColumns: '1.3fr 1fr', marginBottom: 16 }}>
        <div className="mx-card">
          <div className="mx-card-head"><h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Accuracy trend <InfoHint text={HELP.accuracyTrend} /></h3></div>
          <div className="mx-card-pad">
            {trend.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gf1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--mx-c1)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="var(--mx-c1)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
                  <XAxis dataKey="i" tick={AXIS} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 1]} tickFormatter={pct} tick={AXIS} axisLine={false} tickLine={false} width={38} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => pct(v as number)} />
                  <Area type="monotone" dataKey="f1" stroke="var(--mx-c1)" strokeWidth={2} fill="url(#gf1)" name="Accuracy" />
                  <Area type="monotone" dataKey="completeness" stroke="var(--mx-c2)" strokeWidth={2} fillOpacity={0} name="Completeness" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="mx-muted" style={{ fontSize: 13 }}>No accuracy data yet.</div>}
          </div>
        </div>

        <div className="mx-card">
          <div className="mx-card-head"><h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Method comparison <InfoHint text={HELP.methodComparison} /></h3></div>
          <div className="mx-card-pad">
            {streams.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={streams} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" vertical={false} />
                  <XAxis dataKey="stream" tickFormatter={prettyStream} tick={AXIS} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 1]} tickFormatter={pct} tick={AXIS} axisLine={false} tickLine={false} width={38} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--mx-surface-2)' }}
                    formatter={(v: any) => pct(v as number)} labelFormatter={prettyStream} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="f1" fill="var(--mx-c1)" radius={[3, 3, 0, 0]} name="Accuracy" />
                  <Bar dataKey="completeness" fill="var(--mx-c2)" radius={[3, 3, 0, 0]} name="Completeness" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="mx-muted" style={{ fontSize: 13 }}>No method data yet.</div>}
          </div>
        </div>
      </div>

      <div className="mx-card">
        <div className="mx-card-head"><h3 className="mx-card-title">Recent runs</h3></div>
        {runs.length ? (
          <table className="mx-table mx-table-hover">
            <thead><tr><th>Document</th><th>Status</th><th>Streams</th><th>Started</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/admin/docshawk/runs/${r.id}`)}>
                  <td style={{ fontWeight: 500 }}>{r.document_filename}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="mx-muted">{(r.streams || []).join(', ')}</td>
                  <td className="mx-subtle">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="mx-card-pad"><EmptyState icon={<Activity size={28} />} title="No runs yet"
          hint="Upload a docshawk document to start the pipeline." /></div>}
      </div>
    </div>
  );
};

export default OverviewDashboard;
